import { Container, Graphics } from 'pixi.js';
import { GAME_CONFIG, COLORS } from '../configs';
export class Player extends Container {
  private bike: Graphics;
  private shield: Graphics | null = null;
  private isFlying: boolean = false;
  private baseY: number = 0;
  private currentLane: number;
  private targetX: number;
  private flashGfx: Graphics | null = null;
  private flashTimer: number = 0;
  constructor() {
    super();
    this.currentLane = GAME_CONFIG.playerStartLane;
    this.targetX = this.getLaneX(this.currentLane);
    this.bike = this.createBike();
    this.addChild(this.bike);
    // Initial position is set externally by Game.ts after stageHeight is known;
    // sensible default leaves the bike in the visual center if init hasn't run.
    this.x = this.targetX;
    this.y = 0;
  }
  private createBike(): Graphics {
    const bike = new Graphics();
    // Shadow under bike
    bike.ellipse(0, 35, 26, 6);
    bike.fill({ color: 0x000000, alpha: 0.25 });
    // Bike chassis (main body)
    bike.roundRect(-20, -15, 40, 30, 6);
    bike.fill(COLORS.player);
    // Bike seat
    bike.roundRect(-10, -14, 22, 10, 3);
    bike.fill(0x1e3a8a);
    // Bike front fairing
    bike.circle(15, -3, 9);
    bike.fill(0x2563eb);
    // Headlight
    bike.circle(20, -3, 3);
    bike.fill(0xfffbeb);
    // Front wheel (filled + rim)
    bike.circle(-12, 16, 11);
    bike.fill(0x111827);
    bike.circle(-12, 16, 11);
    bike.stroke({ width: 2, color: 0x9ca3af });
    // Rear wheel (filled + rim)
    bike.circle(13, 16, 11);
    bike.fill(0x111827);
    bike.circle(13, 16, 11);
    bike.stroke({ width: 2, color: 0x9ca3af });
    // Exhaust pipe
    bike.roundRect(-22, 0, 8, 6, 2);
    bike.fill(0x6b7280);
    // Rider head
    bike.circle(2, -22, 8);
    bike.fill(0xfcd9b6);
    // Rider helmet
    bike.circle(2, -25, 9);
    bike.fill(0x111827);
    // Helmet visor
    bike.roundRect(-5, -27, 14, 4, 2);
    bike.fill(0x22d3ee);
    // Rider torso/jacket
    bike.roundRect(-6, -16, 14, 14, 3);
    bike.fill(0xdc2626);
    return bike;
  }
  private getLaneX(lane: number): number {
    return (lane - 1) * GAME_CONFIG.laneWidth;
  }
  /** Called by Game.ts once the stage dimensions are known. */
  setBaseY(y: number): void {
    this.baseY = y;
    if (!this.isFlying) {
      this.y = y;
    }
  }
  moveLeft(): void {
    if (this.currentLane > 0) {
      this.currentLane -= 1;
      this.targetX = this.getLaneX(this.currentLane);
    }
  }
  moveRight(): void {
    if (this.currentLane < GAME_CONFIG.lanes - 1) {
      this.currentLane += 1;
      this.targetX = this.getLaneX(this.currentLane);
    }
  }
  update(delta: number): void {
    const dx = this.targetX - this.x;
    // Smooth lane transition
    this.x += dx * Math.min(1, 0.18 * delta);
    // Auto-capture the ground line on the first update where Game.ts has set y
    // but setBaseY was never called. This guarantees setFlying(false) snaps the
    // bike back to its proper ground position even without an explicit call.
    if (!this.isFlying && this.baseY === 0 && this.y > 0) {
      this.baseY = this.y;
    }
    if (this.flashTimer > 0) {
      this.flashTimer -= delta;
      if (this.flashTimer <= 0 && this.flashGfx) {
        this.removeChild(this.flashGfx);
        this.flashGfx.destroy();
        this.flashGfx = null;
        this.bike.alpha = 1;
      }
    }
  }
  flash(durationFrames = 12): void {
    this.bike.alpha = 0.4;
    this.flashTimer = durationFrames;
  }
  activateShield(): void {
    if (this.shield) {
      this.shield.destroy();
      this.removeChild(this.shield);
    }
    this.shield = new Graphics();
    this.shield.circle(0, 0, 46);
    this.shield.stroke({ width: 4, color: 0xfde047, alpha: 0.9 });
    this.shield.circle(0, 0, 42);
    this.shield.stroke({ width: 2, color: 0xffffff, alpha: 0.6 });
    this.addChildAt(this.shield, 0);
  }
  deactivateShield(): void {
    if (this.shield) {
      this.removeChild(this.shield);
      this.shield.destroy();
      this.shield = null;
    }
  }
  setFlying(flying: boolean): void {
    if (this.isFlying === flying) return;
    this.isFlying = flying;
    if (flying) {
      // Capture ground on the very first take-off in case update() hasn't
      // observed the rest position yet (defensive against order-of-init).
      if (this.baseY === 0 && this.y > 0) {
        this.baseY = this.y;
      }
      // Lift the bike only 35px above the ground line so the rider and oncoming
      // obstacles stay clearly in view while soaring.
      this.y = this.baseY - 35;
    } else {
      // Always return to the captured ground line; never to (0, 0).
      this.y = this.baseY;
    }
    this.bike.rotation = flying ? -0.12 : 0;
  }
  hasShield(): boolean {
    return this.shield !== null;
  }
  getLane(): number {
    return this.currentLane;
  }
  getIsFlying(): boolean {
    return this.isFlying;
  }
  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - 22,
      y: this.y - 36,
      width: 44,
      height: 56,
    };
  }
}